/**
 * Journal date formatting (YYYY-MM-DD keys → localized text).
 */
import { parseDateKey } from "@journal/shared";

export function formatDayHeading(dateKey: string, locale: string): string {
  const date = parseDateKey(dateKey);
  if (!date) {
    return dateKey;
  }
  return new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(date);
}

export function formatDayShort(dateKey: string, locale: string): string {
  const date = parseDateKey(dateKey);
  if (!date) {
    return dateKey;
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
}

export function formatMonthHeading(year: number, monthIndex: number, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(
    new Date(year, monthIndex, 1),
  );
}

export function formatEntryTime(iso: string | null, locale: string): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit" }).format(date);
}

/**
 * Short relative time ("5 min ago", "yesterday") for version history.
 * Falls back to a short absolute date from ~7 days onward.
 */
export function formatRelativeTime(iso: string | null, locale: string, now = new Date()): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const MINUTE_S = 60;
  const HOUR_S = 3600;
  const DAY_S = 86_400;
  const WEEK_S = 604_800;

  const elapsedSeconds = Math.round((date.getTime() - now.getTime()) / 1000);
  const magnitude = Math.abs(elapsedSeconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (magnitude < MINUTE_S) {
    return rtf.format(Math.trunc(elapsedSeconds), "second");
  }
  if (magnitude < HOUR_S) {
    return rtf.format(Math.trunc(elapsedSeconds / MINUTE_S), "minute");
  }
  if (magnitude < DAY_S) {
    return rtf.format(Math.trunc(elapsedSeconds / HOUR_S), "hour");
  }
  if (magnitude < WEEK_S) {
    return rtf.format(Math.trunc(elapsedSeconds / DAY_S), "day");
  }
  return new Intl.DateTimeFormat(locale, {
    day: "numeric",
    month: "short",
    year: date.getFullYear() === now.getFullYear() ? undefined : "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}
